import React, { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import ApiService from "../../services/api-service";
import { getUserId } from "../../constants/function";
import { formatCount, daysUntil } from "../../utils/gamification/format";
import { FAN_LEVELS, levelProgress, levelName, statusesForArtist, streakList, STATUS_LABELS, STREAK_LABELS } from "../../utils/gamification/levels";
import { HairlineBar, INK, Muted } from "../Ink";
import BadgeGlyph from "./BadgeGlyph";

// Viewer's fan progression from `/engagement/me`. On an artist profile pass
// `artistId` to also show the statuses the viewer holds for that artist.
export default function FanProgression({ artistId, artistName, sx }) {
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!getUserId()) return undefined;
    let alive = true;
    ApiService.ENGAGEMENT_ME()
      .then((res) => alive && setMe(res?.result ?? res ?? null))
      .catch((error) => console.error("[FanProgression] /engagement/me failed", error?.message));
    return () => {
      alive = false;
    };
  }, []);

  if (!me) return null;

  const points = Number(me.fanPoints) || 0;
  const computed = levelProgress(points, FAN_LEVELS);
  const level = levelName(me.fanLevel) || computed.level.name;
  const next = levelName(me.nextLevel) || (computed.next ? computed.next.name : "");
  const pointsToNext = Number.isFinite(Number(me.pointsToNext)) ? Number(me.pointsToNext) : computed.pointsToNext;
  const span = computed.next ? computed.next.min - computed.level.min : 1;
  const streaks = streakList(me.streaks);
  const badges = Array.isArray(me.badges) ? me.badges : [];
  const statuses = statusesForArtist(me.statuses, artistId);

  return (
    <Box sx={{ ...sx }}>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: INK.text, letterSpacing: "-0.01em" }}>{level}</Typography>
        <Muted>
          {formatCount(points)} pts{next ? ` · ${formatCount(pointsToNext)} to ${next}` : " · top level"}
        </Muted>
      </Stack>
      <HairlineBar value={next ? span - pointsToNext : 1} max={next ? span : 1} sx={{ mt: 1, mb: 1.25 }} />

      {streaks.length > 0 ? (
        <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
          {streaks.map((s) => {
            const left = daysUntil(s.expiresAt);
            return (
              <Typography key={s.key} sx={{ fontSize: 12, color: INK.muted, fontVariantNumeric: "tabular-nums" }}>
                <Box component="span" sx={{ color: INK.text, fontWeight: 700 }}>{Number(s.current) || 0}</Box>
                {" "}{STREAK_LABELS[s.key] || s.key}
                {" · longest "}{Number(s.longest) || 0}
                {left !== null && (Number(s.current) || 0) > 0 ? ` · ${left === 0 ? "ends today" : `${left}d left`}` : ""}
              </Typography>
            );
          })}
        </Stack>
      ) : null}

      {badges.length > 0 ? (
        <Box sx={{ mt: 1.25, display: "flex", flexWrap: "wrap" }}>
          {badges.map((b, i) => <BadgeGlyph key={b?.key || b?._id || i} badge={b} />)}
        </Box>
      ) : null}

      {artistId && statuses.length > 0 ? (
        <Typography sx={{ mt: 1, fontSize: 12, color: INK.muted }}>
          {artistName ? `With ${artistName}: ` : "Here: "}
          <Box component="span" sx={{ color: INK.text }}>{statuses.map((k) => STATUS_LABELS[k] || k).join(" · ")}</Box>
        </Typography>
      ) : null}
    </Box>
  );
}
